/**
 * TenantFeatureFlagsSection
 *
 * Per-tenant feature-flag override surface mounted on TenantDetailPage.
 * Each flag row exposes a 3-state RadioGroup (Default / Force ON / Force OFF)
 * wired to setTenantOverride / removeTenantOverride. Critical flags are gated
 * behind the typed-confirmation dialog before any write is dispatched.
 *
 * Audit history per flag: a single platform-audit-logs query is fired the
 * first time any row's accordion is expanded; results are filtered
 * client-side per flag.key. v1 fetches up to 50 entries; if volume grows we
 * push a flagKey filter down to the backend (design.md decision #4).
 */

import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import {
  useTenantFlags,
  useSetTenantOverride,
  useRemoveTenantOverride,
  usePlatformAuditLogs,
} from '../../hooks';
import { CriticalFlagConfirmDialog } from '../feature-flags/CriticalFlagConfirmDialog';
import type { TenantFeatureFlag, PlatformAuditLog } from '../../types';

type RadioValue = 'default' | 'on' | 'off';

interface PendingChange {
  flag: TenantFeatureFlag;
  next: RadioValue;
}

interface TenantFeatureFlagsSectionProps {
  tenantId: string;
}

const radioValueForFlag = (flag: TenantFeatureFlag): RadioValue => {
  if (!flag.hasOverride) return 'default';
  return flag.effectiveValue ? 'on' : 'off';
};

const formatTransitionPart = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return 'default';
  return value ? 'true' : 'false';
};

const formatPerformedAt = (iso: string): string => {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, 'dd.MM.yyyy HH:mm');
  } catch {
    return 'Invalid date';
  }
};

const formatPerformer = (entry: PlatformAuditLog): string => {
  const { firstName, lastName, email } = entry.user;
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  return name.length > 0 ? name : email;
};

export function TenantFeatureFlagsSection({
  tenantId,
}: TenantFeatureFlagsSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const flagsQuery = useTenantFlags(tenantId);
  const setOverride = useSetTenantOverride();
  const removeOverride = useRemoveTenantOverride();

  const [pendingChange, setPendingChange] = useState<PendingChange | null>(
    null
  );
  const [auditEnabled, setAuditEnabled] = useState(false);

  const auditQuery = usePlatformAuditLogs(
    { action: 'FEATURE_FLAG_UPDATE', targetTenantId: tenantId, pageSize: 50 },
    { enabled: auditEnabled }
  );

  const auditEntriesByFlagKey = useMemo(() => {
    const grouped: Record<string, PlatformAuditLog[]> = {};
    const results = auditQuery.data?.results ?? [];
    for (const entry of results) {
      const flagKey =
        entry.details && typeof entry.details === 'object'
          ? ((entry.details as Record<string, unknown>).flagKey as
              | string
              | undefined)
          : undefined;
      if (!flagKey) continue;
      (grouped[flagKey] ??= []).push(entry);
    }
    return grouped;
  }, [auditQuery.data]);

  const dispatchChange = async (flag: TenantFeatureFlag, next: RadioValue) => {
    try {
      if (next === 'default') {
        await removeOverride.mutateAsync({ key: flag.key, tenantId });
        enqueueSnackbar(`Reset ${flag.key} to default for this tenant`, {
          variant: 'success',
        });
      } else {
        const value = next === 'on';
        await setOverride.mutateAsync({ key: flag.key, tenantId, value });
        enqueueSnackbar(
          `${flag.key} forced ${value ? 'ON' : 'OFF'} for this tenant`,
          { variant: 'success' }
        );
      }
    } catch {
      enqueueSnackbar(`Failed to update override for ${flag.key}`, {
        variant: 'error',
      });
    }
  };

  const handleRadioChange = (flag: TenantFeatureFlag, next: RadioValue) => {
    if (next === radioValueForFlag(flag)) return;
    if (flag.isCritical) {
      setPendingChange({ flag, next });
      return;
    }
    void dispatchChange(flag, next);
  };

  const handleConfirmCritical = async () => {
    if (!pendingChange) return;
    const { flag, next } = pendingChange;
    setPendingChange(null);
    await dispatchChange(flag, next);
  };

  const handleCancelCritical = () => {
    setPendingChange(null);
  };

  const isMutating = setOverride.isPending || removeOverride.isPending;
  const flags = flagsQuery.data ?? [];

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Feature Flags
        </Typography>

        {flagsQuery.isLoading && (
          <Box>
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={72}
                sx={{ mb: 1, borderRadius: 1 }}
              />
            ))}
          </Box>
        )}

        {flagsQuery.isError && (
          <Alert severity="error">
            Failed to load feature flags for this tenant.
          </Alert>
        )}

        {!flagsQuery.isLoading && !flagsQuery.isError && flags.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 4, textAlign: 'center' }}
          >
            No feature flags configured
          </Typography>
        )}

        {!flagsQuery.isLoading && !flagsQuery.isError && flags.length > 0 && (
          <List disablePadding>
            {flags.map((flag, index) => {
              const radioValue = radioValueForFlag(flag);
              const auditEntries = auditEntriesByFlagKey[flag.key] ?? [];
              const showAuditLoader = auditEnabled && auditQuery.isLoading;
              const showAuditError = auditEnabled && auditQuery.isError;

              return (
                <ListItem
                  key={flag.key}
                  divider={index < flags.length - 1}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 1,
                    py: 2,
                    px: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 240 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ flexWrap: 'wrap' }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 500,
                          }}
                        >
                          {flag.key}
                        </Typography>
                        {flag.isCritical && (
                          <Chip
                            label="CRITICAL"
                            color="warning"
                            size="small"
                            icon={<WarningIcon fontSize="small" />}
                          />
                        )}
                        <Chip
                          label={flag.effectiveValue ? 'ON' : 'OFF'}
                          size="small"
                          color={flag.effectiveValue ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {flag.description || '-'}
                      </Typography>
                    </Box>

                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={radioValue}
                        onChange={(e) =>
                          handleRadioChange(flag, e.target.value as RadioValue)
                        }
                      >
                        <FormControlLabel
                          value="default"
                          disabled={isMutating}
                          control={<Radio size="small" />}
                          label={`Default (inherits ${flag.defaultValue ? 'ON' : 'OFF'})`}
                        />
                        <FormControlLabel
                          value="on"
                          disabled={isMutating}
                          control={<Radio size="small" />}
                          label="Force ON"
                        />
                        <FormControlLabel
                          value="off"
                          disabled={isMutating}
                          control={<Radio size="small" />}
                          label="Force OFF"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  <Accordion
                    elevation={0}
                    disableGutters
                    square
                    onChange={(_, expanded) => {
                      if (expanded) setAuditEnabled(true);
                    }}
                    sx={{
                      bgcolor: 'transparent',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 0,
                        minHeight: 'auto',
                        '& .MuiAccordionSummary-content': { my: 0.5 },
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        View history
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, py: 1 }}>
                      {showAuditLoader && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 1,
                          }}
                        >
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            Loading history…
                          </Typography>
                        </Box>
                      )}
                      {showAuditError && (
                        <Alert severity="error">
                          Failed to load audit history.
                        </Alert>
                      )}
                      {!showAuditLoader &&
                        !showAuditError &&
                        auditEntries.length === 0 && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 1 }}
                          >
                            No history yet for this flag
                          </Typography>
                        )}
                      {!showAuditLoader &&
                        !showAuditError &&
                        auditEntries.length > 0 && (
                          <List dense disablePadding>
                            {auditEntries.map((entry, entryIdx) => {
                              const details =
                                (entry.details as Record<string, unknown>) ?? {};
                              const previous = formatTransitionPart(
                                details.previousValue as
                                  | boolean
                                  | null
                                  | undefined
                              );
                              const next = formatTransitionPart(
                                details.newValue as boolean | null | undefined
                              );
                              return (
                                <Box key={entry.id}>
                                  {entryIdx > 0 && <Divider />}
                                  <ListItem sx={{ px: 0, py: 1 }}>
                                    <Box sx={{ width: '100%' }}>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{ flexWrap: 'wrap' }}
                                      >
                                        <Typography
                                          variant="body2"
                                          fontWeight={500}
                                        >
                                          {formatPerformer(entry)}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {formatPerformedAt(entry.performedAt)}
                                        </Typography>
                                      </Stack>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontFamily: 'monospace' }}
                                      >
                                        {previous} → {next}
                                      </Typography>
                                    </Box>
                                  </ListItem>
                                </Box>
                              );
                            })}
                          </List>
                        )}
                    </AccordionDetails>
                  </Accordion>
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>

      <CriticalFlagConfirmDialog
        key={pendingChange?.flag.key ?? 'idle'}
        open={!!pendingChange}
        flagKey={pendingChange?.flag.key ?? ''}
        action={
          pendingChange
            ? pendingChange.next === 'default'
              ? 'reset to default'
              : `force ${pendingChange.next.toUpperCase()}`
            : ''
        }
        onConfirm={handleConfirmCritical}
        onCancel={handleCancelCritical}
      />
    </Card>
  );
}
